export type Frequency = "daily" | "weekly" | "monthly" | "custom";

export interface SchedForm {
  frequency: Frequency;
  hour: number;
  minute: number;
  days: number[];
  dayOfMonth: number;
  customCron: string;
}

export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const SCHEDULE_MINUTES = [0, 15, 30, 45];

const idxToCronDay = (idx: number): number => (idx === 6 ? 0 : idx + 1);
const cronDayToIdx = (n: number): number => (n === 0 || n === 7 ? 6 : n - 1);

export function daysToCron(selected: number[]): string {
  const sorted = [...selected].sort((a, b) => a - b);
  const isWeekdays = sorted.length === 5 && [0, 1, 2, 3, 4].every((d) => sorted.includes(d));
  if (isWeekdays) return "1-5";
  return sorted.map(idxToCronDay).sort((a, b) => a - b).join(",");
}

export function parseCron(expr: string | undefined): SchedForm {
  const base: SchedForm = {
    frequency: "daily", hour: 8, minute: 0,
    days: [0, 1, 2, 3, 4], dayOfMonth: 1, customCron: expr ?? "",
  };
  if (!expr) return base;
  const t = expr.trim().split(/\s+/);
  if (t.length !== 5) return { ...base, frequency: "custom", customCron: expr };
  const [min, hour, dom, mon, dow] = t;
  if (!/^\d+$/.test(min) || !/^\d+$/.test(hour) || mon !== "*") {
    return { ...base, frequency: "custom", customCron: expr };
  }
  const hh = Number(hour);
  const mm = Number(min);
  if (dom === "*" && dow === "*") return { ...base, frequency: "daily", hour: hh, minute: mm };
  if (dom === "*" && dow !== "*") {
    let days: number[];
    if (dow === "1-5") {
      days = [0, 1, 2, 3, 4];
    } else {
      const parsed = dow.split(",").map((d) => Number(d));
      if (parsed.some((n) => !Number.isInteger(n) || n < 0 || n > 7)) {
        return { ...base, frequency: "custom", customCron: expr };
      }
      days = parsed.map(cronDayToIdx);
    }
    return { ...base, frequency: "weekly", hour: hh, minute: mm, days };
  }
  if (/^\d+$/.test(dom) && dow === "*") {
    return { ...base, frequency: "monthly", hour: hh, minute: mm, dayOfMonth: Number(dom) };
  }
  return { ...base, frequency: "custom", customCron: expr };
}

export function formToCron(f: SchedForm): string {
  if (f.frequency === "custom") return f.customCron.trim();
  const time = `${f.minute} ${f.hour}`;
  if (f.frequency === "daily") return `${time} * * *`;
  if (f.frequency === "weekly") return `${time} * * ${f.days.length ? daysToCron(f.days) : "1-5"}`;
  return `${time} ${f.dayOfMonth} * *`;
}

function isValidCronField(field: string, min: number, max: number): boolean {
  if (field === "*") return true;
  return field.split(",").every((part) => {
    const stepMatch = part.match(/^(.+)\/(\d+)$/);
    let range = part;
    if (stepMatch) {
      if (Number(stepMatch[2]) < 1) return false;
      range = stepMatch[1];
    }
    if (range === "*") return true;
    const rangeMatch = range.match(/^(\d+)(?:-(\d+))?$/);
    if (!rangeMatch) return false;
    const start = Number(rangeMatch[1]);
    if (start < min || start > max) return false;
    if (rangeMatch[2] !== undefined) {
      const end = Number(rangeMatch[2]);
      if (end < min || end > max || end < start) return false;
    }
    return true;
  });
}

export function isValidCron(expr: string): boolean {
  const t = expr.trim().split(/\s+/);
  if (t.length !== 5) return false;
  const [min, hour, dom, mon, dow] = t;
  return (
    isValidCronField(min, 0, 59) &&
    isValidCronField(hour, 0, 23) &&
    isValidCronField(dom, 1, 31) &&
    isValidCronField(mon, 1, 12) &&
    isValidCronField(dow, 0, 7)
  );
}

export function utcToLocalHHMM(hour: number, minute: number): string {
  const d = new Date();
  d.setUTCHours(hour, minute, 0, 0);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
